// PeopleService.js - ACTUALIZADO con funcionalidad showDeleted y restricciones

import { customFetch } from '../../customFetch';
import dayjs from 'dayjs';

// Importamos las URLs de la API
import {
  PERSONS,
  DOCUMENTS,
  INSPECTIONS,
  WORKS,
  TRAININGS,
  TOOLS
} from '../../utils/URLS';

const PeopleService = {
  /**
   * Método para obtener todas las personas
   * @param {Object} options - Opciones de consulta
   * @param {boolean} options.showDeleted - Si mostrar personas dadas de baja
   * @returns {Promise<Array>} Lista de personas
   */
  async getAllPeople(options = {}) {
    try {
      // Construir URL con parámetros si es necesario
      let url = PERSONS.LIST;
      const params = new URLSearchParams();
      
      if (options.showDeleted === true) {
        params.append('showDeleted', 'true');
      }
      
      // Si hay parámetros, agregarlos a la URL
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await customFetch(url, {});
      return await response.json();
    } catch (error) {
      console.error('Error en getAllPeople:', error);
      throw error;
    }
  },

  /**
   * Obtener la lista de nombres de documentos disponibles
   * @returns {Promise<string[]>} - Lista de nombres de documentos
   */
  async getDocumentNames() {
    try {
      const response = await customFetch(DOCUMENTS.NAMES, {});
      return await response.json();
    } catch (error) {
      console.error('Error en getDocumentNames:', error);
      // Devolver un array vacío en caso de error para no romper el formulario
      return [];
    }
  },

  // ✅ Método específico para edición - Solo datos básicos de la persona
  async getPersonForEdit(id) {
    try {
      //console.log(`📝 Cargando datos básicos para edición de persona ${id}`);
      
      // Solo hacer 1 request - datos básicos de la persona
      const response = await customFetch(`${PERSONS.V2}${id}`, {});
      const person = await response.json();
      
      //console.log(`✅ Datos básicos de persona ${id} cargados para edición`);
      return person;
    } catch (error) {
      console.error('Error en getPersonForEdit:', error);
      throw error;
    }
  },

  // Método para obtener todos los datos de una persona (PARA PERFIL COMPLETO)
  async getPerson(id) {
    try {
      //console.log(`👤 Cargando perfil completo de persona ${id}`);
      
      // Definimos los URLs para las diferentes peticiones
      const urls = [
        `${PERSONS.V2}${id}`,               // Datos personales
        `${DOCUMENTS.BY_PERSON}${id}`,       // Documentos
        `${INSPECTIONS.BY_PERSON}${id}`,     // Inspecciones
        `${WORKS.BY_PERSON}${id}`,           // Obras
        `${TRAININGS.BY_PERSON}${id}`,       // Capacitaciones
        `${TOOLS.BY_PERSON}${id}`            // Herramientas
      ];

      // Realizamos todas las peticiones en paralelo
      const responses = await Promise.all(
        urls.map((url) => customFetch(url, {}))
      );

      // Convertimos todas las respuestas a JSON
      const responseData = await Promise.all(
        responses.map((response) => response.json())
      );

      // Destructuramos los datos recibidos
      const [person, documents, inspections, works, trainings, tools] = responseData;

      // Construimos el objeto de datos completo
      const data = {
        ...person,
        documents,
        inspections,
        works,
        trainings,
        // Filtramos las herramientas retiradas
        tools: tools.filter((tool) => !tool.retired),
      };

      //console.log(`✅ Perfil completo de persona ${id} cargado`);
      return data;
    } catch (error) {
      console.error('Error en getPerson:', error);
      throw error;
    }
  },

  // Método para crear una nueva persona
  async createPerson(personData) {
    try {
      // Correcciones para cumplir con la API:
      personData.phone = parseInt(personData.phone, 10);
      if (personData.entryDate) {
        personData.entryDate = dayjs(personData.entryDate).format('YYYY-MM-DD');
      }
      
      if (personData.email === '') {
        personData.email = null; // Si el email está vacío, lo dejamos como null 
      }  
      personData.surname = personData.surname ? String(personData.surname) : "";

      const url = personData.type === 'F' 
        ? PERSONS.CREATE 
        : PERSONS.COMPANY_CREATE;
        
      const response = await customFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(personData)
      });
      
      return await response.json();
    } catch (error) {
      console.error('Error en createPerson:', error);
      throw error;
    }
  },

  /**
   * Método para actualizar una persona - SIN RESTRICCIONES (permite editar personas dadas de baja)
   * @param {string} id - ID de la persona
   * @param {Object} personData - Nuevos datos de la persona
   * @returns {Promise<Object>} Persona actualizada
   */
  async updatePerson(id, personData) {
    try {
      const url = personData.type === 'F'
        ? `${PERSONS.UPDATE}${id}`
        : `${PERSONS.COMPANY_UPDATE}${id}`;

      //Forzar phone a ser un número entero 
      personData.phone = parseInt(personData.phone, 10);
      // Forzar entryDate a ser una fecha ISO si existe
      if (personData.entryDate) { 
        personData.entryDate = dayjs(personData.entryDate).format('YYYY-MM-DD');
      }   
      if (personData.email === '') {
        personData.email = null; // Si el email está vacío, lo dejamos como null 
      }  
        
      const response = await customFetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(personData)
      });
      
      return await response.json();
    } catch (error) {
      console.error(`Error en updatePerson (ID: ${id}):`, error);
      throw error;
    }
  },

  /**
   * Método para eliminar una persona (dar de baja)
   * @param {string} id - ID de la persona
   * @returns {Promise<Object>} Persona eliminada
   */
  async deletePerson(id) {
    try {
      const response = await customFetch(`${PERSONS.LIST}${id}`, {
        method: 'DELETE'
      });
      
      return await response.json();
    } catch (error) {
      console.error(`Error en deletePerson (ID: ${id}):`, error);
      throw error;
    }
  },

  /**
   * ✅ NUEVO: Restaurar una persona dada de baja
   * @param {string} id - ID de la persona a restaurar
   * @returns {Promise<Object>} Resultado de la operación
   */
  async restorePerson(id) {
    try {
      // Usar fetch directamente para manejar mejor los códigos de error
      const token = sessionStorage.getItem('access_token');
      const response = await fetch(`${PERSONS.LIST}${id}/restore`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Procesar la respuesta independientemente del código de estado
      const data = await response.json();
      
      return {
        statusCode: response.status,
        data: data
      };
    } catch (error) {
      console.error(`Error en restorePerson (ID: ${id}):`, error);
      
      // Para errores de red o parsing, devolver formato consistente
      return {
        statusCode: 0,
        data: { 
          message: error.message || 'Error de conexión al restaurar la persona'
        }
      };
    }
  },

  // Método para obtener la imagen de perfil
  async getProfilePicture(id) {
    try {
      const response = await customFetch(`${PERSONS.LIST}${id}/picture`, {});
      return URL.createObjectURL(await response.blob());
    } catch (error) {
      console.error(`Error en getProfilePicture (ID: ${id}):`, error);
      throw error;
    }
  },

  /**
   * Método para subir una imagen de perfil - CON RESTRICCIÓN
   * @param {string} id - ID de la persona
   * @param {File} pictureFile - Archivo de imagen
   * @returns {Promise<Object>} Resultado de la operación
   */
  async uploadProfilePicture(id, pictureFile) {
    try {
      // 🔒 VERIFICAR SI LA PERSONA ESTÁ DADA DE BAJA
      const person = await this.getPersonForEdit(id);
      if (person.deletedDate) {
        return {
          success: false,
          statusCode: 403,
          data: { 
            message: 'No se puede cambiar la foto de una persona dada de baja',
            error: 'PERSON_DEACTIVATED' 
          }
        };
      }

      const formData = new FormData();
      formData.append('picture', pictureFile);
      
      const response = await customFetch(`${PERSONS.LIST}${id}/upload-picture`, {
        method: 'POST',
        body: formData
      });
      
      return await response.json();
    } catch (error) {
      console.error(`Error en uploadProfilePicture (ID: ${id}):`, error);
      throw error;
    }
  },

  /**
   * Método para subir un documento (con o sin archivo) - CON RESTRICCIÓN
   * @param {File|null} file - Archivo del documento (opcional)
   * @param {Object} documentData - Metadatos del documento
   * @param {string} personId - ID de la persona
   * @returns {Promise<Object>} Resultado de la operación
   */
  async uploadDocument(file, documentData, personId) {
    if (!personId) {
      console.error('ID de persona no proporcionado');
      return { success: false, error: 'ID no proporcionado' };
    }
    
    try {
      // 🔒 VERIFICAR SI LA PERSONA ESTÁ DADA DE BAJA
      const person = await this.getPersonForEdit(personId);
      if (person.deletedDate) {
        return {
          success: false,
          statusCode: 403,
          data: { 
            message: 'No se pueden agregar documentos a una persona dada de baja',
            error: 'PERSON_DEACTIVATED' 
          }
        };
      }

      const form = new FormData();
      if (file) {
        form.append('file', file);
      }
      form.append('name', documentData.name);
      form.append('endDate', dayjs(documentData.endDate).format('YYYY-MM-DD'));
      form.append('observations', documentData.observations || '');

      const requestOptions = {
        method: 'POST',
        body: form
      };
      
      // Usar la URL correcta: PERSONS.LIST/{id}/upload-doc
      const response = await customFetch(`${PERSONS.LIST}${personId}/upload-doc`, requestOptions);
      const data = await response.json();
      
      return {
        success: response.ok,
        statusCode: response.status,
        data: data
      };
    } catch (error) {
      console.error(`Error en uploadDocument (PersonID: ${personId}):`, error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Método para actualizar un documento existente - CON RESTRICCIÓN
   * @param {File|null} file - Nuevo archivo (opcional)
   * @param {Object} documentData - Nuevos metadatos
   * @param {string} documentId - ID del documento
   * @returns {Promise<Object>} Resultado de la operación
   */
  async updateDocument(file, documentData, documentId) {
    if (!documentId) {
      return { success: false, error: 'ID de documento no proporcionado' };
    }
    
    try {
      // 🔒 Nota: Idealmente verificar si la persona asociada está dada de baja
      // Requeriría obtener el personId del documento primero
      
      if (file) {
        // Si hay un nuevo archivo, actualizar archivo y metadatos
        const form = new FormData();
        form.append('file', file);
        form.append('name', documentData.name);
        form.append('endDate', dayjs(documentData.endDate).format('YYYY-MM-DD'));
        form.append('observations', documentData.observations || '');

        const response = await customFetch(`${DOCUMENTS.LIST}${documentId}/update-file`, {
          method: 'PUT',
          body: form
        });
        
        const data = await response.json();
        return {
          success: response.ok,
          statusCode: response.status,
          data: data
        };
      } else {
        // Si no hay nuevo archivo, solo actualizar metadatos
        const response = await customFetch(`${DOCUMENTS.LIST}${documentId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: documentData.name,
            endDate: dayjs(documentData.endDate).format('YYYY-MM-DD'),
            observations: documentData.observations || ''
          })
        });
        
        const data = await response.json();
        return {
          success: response.ok,
          statusCode: response.status,
          data: data
        };
      }
    } catch (error) {
      console.error(`Error en updateDocument (DocumentID: ${documentId}):`, error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Método para eliminar un documento - CON RESTRICCIÓN
   * @param {string} documentId - ID del documento
   * @returns {Promise<Object>} Documento eliminado
   */
  async deleteDocument(documentId) {
    try {
      // 🔒 Nota: Idealmente verificar si la persona asociada está dada de baja
      
      const response = await customFetch(`${DOCUMENTS.LIST}${documentId}`, {
        method: 'DELETE'
      });
      
      return await response.json();
    } catch (error) {
      console.error(`Error en deleteDocument (ID: ${documentId}):`, error);
      throw error;
    }
  },

  /**
   * Descargar/visualizar un documento
   * @param {string} documentId - ID del documento
   * @param {boolean} forceDownload - Forzar descarga
   * @param {string|null} documentName - Nombre del documento
   * @returns {Promise<Object>} Resultado de la operación
   */
  async getDocument(documentId, forceDownload = false, documentName = null) {
    try {
      const response = await customFetch(`${DOCUMENTS.LIST}${documentId}`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const contentType = response.headers.get('Content-Type') || blob.type;
      
      //console.log(`📄 Procesando documento ID: ${documentId}, tipo: ${contentType}`);
      //console.log(`🔍 forceDownload: ${forceDownload}`);

      // ✅ USAR LA LÓGICA ORIGINAL QUE FUNCIONABA PARA VISUALIZAR
      const canViewInBrowser = this.isViewableInBrowser('', contentType);
      //console.log(`👁️ canViewInBrowser: ${canViewInBrowser}`);
      
      if (!forceDownload && canViewInBrowser) {
        // ✅ VISUALIZAR: Exactamente como funcionaba antes
        const blobUrl = URL.createObjectURL(blob);
        const newWindow = window.open(blobUrl, '_blank');
        
        if (newWindow) {
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
          }, 10000);
          
          //console.log(`👀 Documento abierto en nueva pestaña para visualización`);
        } else {
          // Si no se puede abrir pestaña, descargar con nombre
          const filename = this.generateFilename(documentName, contentType);
          this.downloadFile(blob, filename);
        }
      } else {
        // ✅ DESCARGAR: Con nombre del campo 'name' + extensión correcta
        const filename = this.generateFilename(documentName, contentType);
        this.downloadFile(blob, filename);
        //console.log(`💾 Forzando descarga: ${filename}`);
      }

      return { 
        success: true, 
        filename: documentName || 'documento',
        action: (!forceDownload && canViewInBrowser) ? 'viewed' : 'downloaded'
      };

    } catch (error) {
      console.error('Error al obtener documento:', error);
      return { 
        success: false, 
        error: error.message || 'Error al obtener el documento' 
      };
    }
  },

  /**
   * Verifica si un archivo se puede visualizar en navegador
   * @param {string} filename - Nombre del archivo (no usado actualmente)
   * @param {string} contentType - Tipo MIME del archivo
   * @returns {boolean} True si se puede visualizar
   */
  isViewableInBrowser(filename, contentType) {
    // Solo verificar por tipo MIME ya que no tenemos filename confiable
    if (contentType) {
      const viewableMimeTypes = [
        'image/', 'application/pdf', 'text/', 'text/plain', 
        'text/csv', 'text/html', 'application/json'
      ];
      
      return viewableMimeTypes.some(type => contentType.startsWith(type));
    }
    
    return false;
  },

  /**
   * Genera nombre de archivo usando campo 'name' + extensión del content-type
   * @param {string|null} documentName - Nombre del documento
   * @param {string} contentType - Tipo MIME
   * @returns {string} Nombre de archivo con extensión correcta
   */
  generateFilename(documentName, contentType) {
    const extensionMap = {
      'application/pdf': '.pdf',
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/png': '.png', 
      'image/gif': '.gif',
      'image/webp': '.webp',
      'image/bmp': '.bmp',
      'text/plain': '.txt',
      'text/csv': '.csv',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
      'application/vnd.ms-excel': '.xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
      'application/zip': '.zip',
      'application/json': '.json'
    };
    
    const correctExtension = extensionMap[contentType] || '';
    
    if (documentName && documentName.trim()) {
      let baseName = documentName.trim();
      
      // Quitar extensión existente (podría estar incorrecta)
      const lastDotIndex = baseName.lastIndexOf('.');
      if (lastDotIndex > 0) {
        baseName = baseName.substring(0, lastDotIndex);
      }
      
      // Agregar extensión correcta detectada del content-type
      return `${baseName}${correctExtension}`;
    }
    
    return `documento${correctExtension}`;
  },

  /**
   * Descarga un archivo usando blob
   * @param {Blob} blob - Blob del archivo
   * @param {string} filename - Nombre del archivo
   */
  downloadFile(blob, filename) {
    try {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      //console.log(`💾 Descargando como: ${filename}`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      //console.log(`✅ Descarga completada: ${filename}`);
    } catch (error) {
      console.error('Error al descargar archivo:', error);
      throw new Error('Error al descargar el archivo');
    }
  },

  /**
   * Asignar obra a persona - CON RESTRICCIÓN
   * @param {string} personId - ID de la persona
   * @param {string} workId - ID de la obra
   * @returns {Promise<Object>} Resultado de la operación
   */
  async assignWorkToPerson(personId, workId) {
    try {
      // 🔒 VERIFICAR SI LA PERSONA ESTÁ DADA DE BAJA
      const person = await this.getPersonForEdit(personId);
      if (person.deletedDate) {
        return {
          success: false,
          statusCode: 403,
          data: { 
            message: 'No se pueden asignar obras a una persona dada de baja',
            error: 'PERSON_DEACTIVATED' 
          }
        };
      }

      // Los endpoints que se ven en la imagen de la API usan PUT para /works/{id}/assign-persons
      // Por lo tanto, usaremos este formato para asignar personas a obras
      const response = await customFetch(`${WORKS.LIST}${workId}/assign-persons`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personsIds: [personId] })
      });
      
      return {
        success: response.ok,
        statusCode: response.status,
        data: await response.json()
      };
    } catch (error) {
      console.error(`Error en assignWorkToPerson (PersonID: ${personId}, WorkID: ${workId}):`, error);
      return { success: false, error: error.message };
    }
  },

  // CAPACITACIONES / TRAININGS

  /**
   * Obtener capacitaciones disponibles para asignar a una persona
   * @param {string} personId - ID de la persona
   * @returns {Promise<Array>} Lista de capacitaciones disponibles
   */
  async getAvailableTrainings(personId) {
    try {
      // Obtener todas las capacitaciones
      const response = await customFetch(TRAININGS.LIST, {});
      const allTrainings = await response.json();
      
      // Obtener capacitaciones ya asignadas a la persona
      const assignedResponse = await customFetch(`${TRAININGS.BY_PERSON}${personId}`, {});
      const assignedTrainings = await assignedResponse.json();
      
      // Filtrar para obtener solo las capacitaciones no asignadas
      const assignedIds = assignedTrainings.map(training => training.id);
      const availableTrainings = allTrainings.filter(training => !assignedIds.includes(training.id));
      
      return availableTrainings;
    } catch (error) {
      console.error('Error en getAvailableTrainings:', error);
      throw error;
    }
  },

  /**
   * Dar de baja a una persona de una capacitación - CON RESTRICCIÓN
   * @param {string} personId - ID de la persona
   * @param {string} trainingId - ID de la capacitación
   * @returns {Promise<Object>} Resultado de la operación
   */
  async unassignPersonFromTraining(personId, trainingId) {
    try {
      // 🔒 VERIFICAR SI LA PERSONA ESTÁ DADA DE BAJA
      const person = await this.getPersonForEdit(personId);
      if (person.deletedDate) {
        return {
          success: false,
          statusCode: 403,
          error: 'No se pueden desasignar capacitaciones de una persona dada de baja'
        };
      }

      // Primero, obtener los datos de la capacitación
      const trainingResponse = await customFetch(`${TRAININGS.LIST}${trainingId}`, {});
      
      if (!trainingResponse.ok) {
        const errorData = await trainingResponse.json();
        return { 
          success: false, 
          error: `Error al obtener datos de la capacitación: ${errorData.message || 'Error desconocido'}`
        };
      }
      
      const trainingData = await trainingResponse.json();
      
      // Asegurarnos de que tenemos un array de IDs de personas
      let currentPersonIds = [];
      
      // Si la capacitación tiene un array de objetos persons, extraer sus IDs
      if (trainingData.persons && Array.isArray(trainingData.persons)) {
        currentPersonIds = trainingData.persons.map(person => person.id);
      } 
      // Si directamente tiene personsIds, usar ese array
      else if (trainingData.personsIds && Array.isArray(trainingData.personsIds)) {
        currentPersonIds = [...trainingData.personsIds];
      }
      
      // Convertir personId a string para asegurar comparación correcta (si viene como número)
      const personIdStr = String(personId);
      
      // Filtrar el ID de la persona que queremos dar de baja
      // Convertimos ambos a string para asegurar comparación correcta
      const updatedPersonIds = currentPersonIds.filter(id => String(id) !== personIdStr);
      
      // Si no había cambios, no es necesario hacer la actualización
      if (updatedPersonIds.length === currentPersonIds.length) {
        console.warn(`La persona (ID: ${personId}) no estaba asignada a esta capacitación (ID: ${trainingId})`);
        return { 
          success: false, 
          error: 'La persona no está asignada a esta capacitación'
        };
      }
      
      // Crear el objeto para actualizar la capacitación
      const updateData = {
        // Mantener todos los datos originales
        type: trainingData.type,
        date: dayjs(trainingData.date).format('YYYY-MM-DD'),
        instructor: trainingData.instructor || '',
        location: trainingData.location || 'generica', // Valor por defecto si no existe
        topic: trainingData.topic || '',
        observations: trainingData.observations || '',
        // Aquí está la clave: la lista actualizada de personas sin la persona a dar de baja
        personsIds: updatedPersonIds
      };
      
      // Enviar la solicitud PUT para actualizar la capacitación
      const response = await customFetch(`${TRAININGS.LIST}${trainingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { 
          success: false, 
          error: `Error al desasignar persona: ${errorData.message || 'Error desconocido'}`
        };
      }
      
      return {
        success: true,
        data: await response.json()
      };
    } catch (error) {
      console.error('Error en unassignPersonFromTraining:', error);
      return { 
        success: false, 
        error: error.message || 'Error desconocido al desasignar persona de la capacitación'
      };
    }
  },

  /**
   * Asignar una persona a una capacitación - CON RESTRICCIÓN
   * @param {string} personId - ID de la persona
   * @param {string} trainingId - ID de la capacitación
   * @returns {Promise<Object>} Resultado de la operación
   */
  async assignPersonToTraining(personId, trainingId) {
    try {
      // 🔒 VERIFICAR SI LA PERSONA ESTÁ DADA DE BAJA
      const person = await this.getPersonForEdit(personId);
      if (person.deletedDate) {
        return {
          success: false,
          statusCode: 403,
          data: { 
            message: 'No se pueden asignar capacitaciones a una persona dada de baja',
            error: 'PERSON_DEACTIVATED' 
          }
        };
      }

      // Obtener los datos de la capacitación
      const trainingResponse = await customFetch(`${TRAININGS.LIST}${trainingId}`, {});
      const trainingData = await trainingResponse.json();
      
      // Obtener la lista actual de personas o inicializar como array vacío
      let currentPersonIds = trainingData.personsIds || [];
      
      // Si no existe personsIds pero existe persons, usar los ids de persons
      if (!currentPersonIds.length && trainingData.persons && trainingData.persons.length) {
        currentPersonIds = trainingData.persons.map(person => person.id);
      }
      
      // Añadir el ID de la persona y eliminar duplicados
      const updatedPersonIds = [...new Set([...currentPersonIds, personId])];
      
      // Actualizar la capacitación con la nueva lista de personas
      const updateData = {
        ...trainingData,
        date: dayjs(trainingData.date).format('YYYY-MM-DD'),
        personsIds: updatedPersonIds
      };
      
      // Eliminar la propiedad persons antes de enviar
      delete updateData.persons;
      
      const response = await customFetch(`${TRAININGS.LIST}${trainingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      
      return {
        success: response.ok,
        data: await response.json()
      };
    } catch (error) {
      console.error('Error en assignPersonToTraining:', error);
      throw error;
    }
  }
};

export default PeopleService;